import { Injectable } from '@angular/core';
import { FormAnswer } from '../models/form.model';
import { RouterParamsService } from '../../shared/services/router-params.service';
import { BaseEntityService } from '../entity-management/services/base/base-entity.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { FormSubmissionsEntityService } from './form-submissions-entity.service';
import { ConfirmService, DataConfirm } from '../../shared/services/app-confirm/confirm.service';
import { FormQuestionEntityService } from './form-question-entity.service';

@Injectable({
  providedIn: 'root',
})
export class FormAnswersEntityService extends BaseEntityService<FormAnswer> {

  constructor(
    private confirmService: ConfirmService,
    private formQuestionEntityService: FormQuestionEntityService,
    private formSubmissionsEntityService: FormSubmissionsEntityService,
    httpClient: HttpClient,
    routerParamsService: RouterParamsService,
  ) {
    super(
      httpClient,
      environment.apiUrl, { singular: 'formAnswer', plural: 'formAnswers', url: 'form-answers' },
      routerParamsService,
    );
    this.nameId = 'formAnswersId';
  }

  getQuestionsWithAnswers(queryParams: any): Observable<unknown> {
    return this.getQuestions(queryParams).pipe(
      switchMap(formQuestions => {
        const { personIds, formSubmissionId } = queryParams;
        return this.formSubmissionsEntityService
          .getWithQuery({ personIds })
          .pipe(
            map(formSubmissions => {
              return {
                formQuestions,
                formSubmissions,
                personIds,
                formSubmissionId,
              };
            }),
          );
      }),
      switchMap(responses => {
        const { formSubmissions, formSubmissionId } = responses;
        let [lastFormSubmission] = formSubmissions;
        if (formSubmissionId) {
          lastFormSubmission = formSubmissions.find(
            fs => fs.id === formSubmissionId,
          );
        }

        if (!lastFormSubmission?.id) {
          return of(responses);
        }

        return this.getWithQuery({
          formSubmissionIds: lastFormSubmission.id.toString(),
          limit: '200',
        }).pipe(
          map(formAnswers => {
            return { ...responses, formAnswers, lastFormSubmission };
          }),
        );
      }),
    );
  }

  getQuestions(queryParams: any): Observable<unknown[]> {
    // The questions list drives label/type/choice resolution on the risk page; a
    // fixed limit silently drops questions when the person matches more than one
    // page. Page through until a batch comes back smaller than the page size.
    return this.getAllPages((offset, limit) =>
      this.formQuestionEntityService.getWithQuery({ ...queryParams, offset, limit }),
    );
  }

  private getAllPages<E>(
    fetchPage: (offset: number, limit: number) => Observable<E[]>,
    limit = 200,
  ): Observable<E[]> {
    const fetchFrom = (offset: number, accumulated: E[]): Observable<E[]> =>
      fetchPage(offset, limit).pipe(
        switchMap(batch => {
          const all = accumulated.concat(batch);
          return batch.length < limit ? of(all) : fetchFrom(offset + limit, all);
        }),
      );
    return fetchFrom(0, []);
  }

  removeResponse(answer: FormAnswer) {
    const data: DataConfirm = {
      buttonConfirm: {
        label: 'Deletar',
        show: true,
      },
      title: 'Deseja realmente remover esta resposta?',
      message: 'Esta ação não pode ser revertida!',
    };
    return this.confirmService.confirm(data).pipe(
      switchMap(response => {
        if (response) {
          return this.delete(answer.id).pipe(map(() => true));
        }
        return of(false);
      }),
    );
  }
}
